import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import ReactMarkdown from 'react-markdown';
import { useLanguage } from '../context/LanguageContext';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch Privacy Policy content from public folder
    setLoading(true);
    fetch('/documentation/Privacy_Policy.md')
      .then(res => {
        if (!res.ok) throw new Error('Failed to load Privacy Policy');
        return res.text();
      })
      .then(text => {
        setContent(text);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading Privacy Policy:', err);
        setContent('# Error Loading Privacy Policy\n\nPlease try again later.');
        setLoading(false);
      });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white border-b-2 border-cyan-500 shadow-md sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="flex items-center gap-2 border-cyan-500 text-cyan-600 hover:bg-cyan-50"
              >
                <ArrowLeft className="h-4 w-4" />
                {language === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
              </Button>
              <div className="flex items-center gap-2">
                <Shield className="h-6 w-6 text-cyan-600" />
                <h1 className="text-2xl font-bold text-gray-800">
                  {language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
                </h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="container mx-auto px-6 py-3">
        <nav className="text-sm text-gray-600">
          <span className="hover:text-cyan-600 cursor-pointer" onClick={() => navigate('/')}>
            {language === 'ar' ? 'الرئيسية' : 'Home'}
          </span>
          <span className="mx-2">/</span>
          <span className="text-cyan-600 font-medium">
            {language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
          </span>
        </nav>
      </div>

      {/* Content */}
      <main className="container mx-auto px-6 py-8 pb-16 max-w-5xl">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12 prose prose-slate prose-cyan max-w-none">
            <ReactMarkdown
              components={{
                h1: ({node, ...props}) => <h1 className="text-4xl font-bold text-cyan-700 mb-6 pb-3 border-b-2 border-cyan-200" {...props} />,
                h2: ({node, ...props}) => <h2 className="text-3xl font-semibold text-cyan-600 mt-8 mb-4" {...props} />,
                h3: ({node, ...props}) => <h3 className="text-2xl font-semibold text-cyan-600 mt-6 mb-3" {...props} />,
                h4: ({node, ...props}) => <h4 className="text-xl font-semibold text-gray-700 mt-4 mb-2" {...props} />,
                p: ({node, ...props}) => <p className="text-gray-700 leading-relaxed mb-4" {...props} />,
                a: ({node, ...props}) => <a className="text-cyan-600 hover:text-cyan-700 underline" {...props} />,
                ul: ({node, ...props}) => <ul className="list-disc list-inside space-y-2 mb-4" {...props} />,
                ol: ({node, ...props}) => <ol className="list-decimal list-inside space-y-2 mb-4" {...props} />,
                li: ({node, ...props}) => <li className="text-gray-700 ml-4" {...props} />,
                strong: ({node, ...props}) => <strong className="text-gray-900 font-semibold" {...props} />,
                blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-cyan-500 pl-4 italic text-gray-600 my-4" {...props} />,
                code: ({node, inline, ...props}) => 
                  inline ? 
                    <code className="bg-cyan-50 text-cyan-800 px-2 py-1 rounded text-sm" {...props} /> :
                    <code className="block bg-gray-900 text-gray-100 p-4 rounded-lg my-4 overflow-x-auto" {...props} />
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        )}
      </main>

      {/* Quick Links Footer */}
      <footer className="container mx-auto px-6 py-8 max-w-5xl">
        <div className="bg-cyan-50 rounded-lg p-6 border border-cyan-200">
          <h3 className="text-lg font-semibold text-cyan-800 mb-3">
            {language === 'ar' ? 'مصادر مفيدة أخرى' : 'Other Helpful Resources'}
          </h3>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/faq')}
              className="border-cyan-500 text-cyan-600 hover:bg-cyan-100"
            >
              {language === 'ar' ? 'الأسئلة الشائعة' : 'FAQs'}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/user-guide')}
              className="border-cyan-500 text-cyan-600 hover:bg-cyan-100"
            >
              {language === 'ar' ? 'دليل المستخدم' : 'User Guide'}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/terms')}
              className="border-cyan-500 text-cyan-600 hover:bg-cyan-100"
            >
              {language === 'ar' ? 'شروط الاستخدام' : 'Terms of Use'}
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
